const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.Utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach((el) => newItems.appendChild(el))

    if (direction == null) {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.element.els['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      .setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      .setAttribute('aria-hidden', (newElements.length > 0).toString())
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.element.els['load-previous'].selector}, ${this.element.els['load-more'].selector}`,
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML =
        window.BAO.Utils.getElement(
          this.element.els['load-previous'].selector,
          { context: html },
        ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML =
        window.BAO.Utils.getElement(this.element.els['load-more'].selector, {
          context: html,
        }).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)
    this.renderProductCount(newHTMLDom)
    this.renderAdditional(newHTMLDom)

    this.element.loading = false
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    // Handle both internal (desktop) and external (mobile) facets
    const internalFacets = this.element.els['facet']?.elements || []
    const externalFacets = this.element.externalEls['facet']?.elements || []
    const allFacets = [...internalFacets, ...externalFacets]

    if (allFacets.length === 0) return

    const getFacetSelector = (facet, isExternal) => {
      const selectorAttr = isExternal ?
        this.element.externalEls.facet.selector :
        this.element.els['facet']?.selector

      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${selectorAttr}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    // Determine which facet submitted the form (could be internal or external)
    let facetThatSubmitted = null
    if (event?.submitter) {
      facetThatSubmitted = event.submitter.closest(
        this.element.els['facet']?.selector || ''
      ) || event.submitter.closest(
        this.element.externalEls.facet.selector
      )
    }

    const facetsToRenderSimply = event && facetThatSubmitted
      ? allFacets.filter((facet) => facet !== facetThatSubmitted)
      : allFacets

    this.element.log('renderFacets', {
      internalFacets,
      externalFacets,
      allFacets,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach((facet) => {
      const isExternal = externalFacets.includes(facet)
      const selector = getFacetSelector(facet, isExternal)

      const el = window.BAO.Utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    // Handle internal active facets (desktop)
    const internalActiveFacets = this.element.els['active-facets']?.elements || []
    // Handle external active facets (mobile)
    const externalActiveFacets = this.element.externalEls['active-facets']?.elements || []
    const allActiveFacets = [...internalActiveFacets, ...externalActiveFacets]

    if (allActiveFacets.length <= 0) return

    allActiveFacets.forEach((activeFacet) => {
      const isExternal = externalActiveFacets.includes(activeFacet)
      const selectorAttr = isExternal ?
        this.element.externalEls['active-facets'].selector :
        this.element.els['active-facets'].selector

      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: selectorAttr,
      }
      if (allActiveFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
        isExternal,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const activeFacetSelector = isExternal ?
        `[data-collection-filters-external-el="active-facet"]` :
        `[${this.element._elIdentifier}="active-facet"]`

      const hasActiveItems = !!window.BAO.Utils.getElement(
        activeFacetSelector,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount (document) {
    const existingProductCounts = window.BAO.Utils.getElements(
      this.element.els['product-count']?.selector,
    )

    const newProductCount = window.BAO.Utils.getElement(
      this.element.els['product-count']?.selector,
      { context: document },
    )
    if (!newProductCount) return

    existingProductCounts.forEach(
      (element) => (element.innerHTML = newProductCount.innerHTML),
    )
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['facets', 'product-grid']
  }

  static get requiredExternalElements () {
    return ['facet', 'facet-summary', 'active-filters', 'clear-all', 'active-facets', 'active-facet', 'apply']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()
    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = window.BAO.Utils.debounce(
      (e) => this.onSubmit(e),
      500,
    )
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(this, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))

    // Also listen to events from the external mobile forms
    const mobileForm = document.getElementById('CollectionFiltersMobileForm')
    if (mobileForm) {
      this.listeners.add(mobileForm, 'input', this.debouncedOnSubmit.bind(this))
      this.listeners.add(mobileForm, 'submit', this.onFormSubmit.bind(this))
    }

    const mobileSortForm = document.getElementById('CollectionSortMobileForm')
    if (mobileSortForm) {
      this.listeners.add(mobileSortForm, 'input', this.debouncedOnSubmit.bind(this))
      this.listeners.add(mobileSortForm, 'submit', this.onFormSubmit.bind(this))
    }

    for (const facet of this.els['facet-trigger'].elements) {
      this.listeners.add(facet, 'click', this.onFacetTriggerClick.bind(this))
    }
  }

  /**
   * @param {MouseEvent} event
   */
  onFacetTriggerClick (event) {
    event.preventDefault()

    const buttonEl = event.target.closest('[data-facet-index]')
    if (!buttonEl) {
      console.log('No button element found with data-facet-index')
      return
    }

    const facetIndex = parseInt(buttonEl.dataset.facetIndex, 10)
    console.log('Facet trigger clicked, index:', facetIndex)

    // while the desktop triggers use 0-based indexing
    const mobileFacet = this.externalEls.facet.elements.find(facetEl => {
      const facetDataIndex = facetEl.dataset.index
      if (!facetDataIndex) return false

      // Extract the number from the data-index (e.g., "mobile-1" -> 1)
      const match = facetDataIndex.match(/-(\d+)$/)
      if (!match) return false

      const mobileFacetIndex = parseInt(match[1], 10)
      return mobileFacetIndex === facetIndex + 1 // Convert 0-based to 1-based
    })

    console.log('Mobile facets available:', this.externalEls.facet.elements.map(f => f.dataset.index))
    console.log('Looking for mobile facet index:', facetIndex + 1)
    console.log('Found mobile facet:', mobileFacet)

    if (!mobileFacet) {
      console.error('No mobile facet found for index:', facetIndex)
      return
    }

    this.closeAllMobileFacets()

    const detailsEl = mobileFacet.querySelector('summary')
    if (!detailsEl) {
      console.error('No summary element found in mobile facet')
      return
    }

    console.log('Setting drawer key and opening facet')

    // Check if window.drawers exists
    if (!window.drawers) {
      console.error('window.drawers is not available')
      return
    }

    // Open the drawer first
    window.drawers.open('mobile-collection-filters')

    // Open the list of options for the facet.
    detailsEl.click()

    console.log('Drawer opened and facet details clicked')
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    // eslint-disable-next-line
    const searchParams = new URLSearchParams(event.target.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    event.preventDefault()

    const parentForm = event.target.closest('form')

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, event })

    if (window.BAO.MediaBreakpoints.isSm) return

    this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const pagination = event.target.closest(this.els?.pagination?.selector)
    if (!pagination) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }

    // Count active filters from both desktop and mobile facets
    const desktopActiveFilters = this.els.facets?.element ?
      this.els.facets.element.querySelectorAll('.clc-ProductGridFacet_Checkbox:checked') : []
    const mobileActiveFilters = this.externalEls.facet.elements.length > 0 ?
      Array.from(this.externalEls.facet.elements).flatMap(facet =>
        Array.from(facet.querySelectorAll('.clc-ProductGridFacet_Checkbox:checked'))
      ) : []

    const totalActiveFilters = [...desktopActiveFilters, ...mobileActiveFilters]

    // Update active filters elements (both internal and external)
    const internalActiveFiltersElements = this.els['active-filters']?.elements || []
    const externalActiveFiltersElements = this.externalEls['active-filters']?.elements || []
    const allActiveFiltersElements = [...internalActiveFiltersElements, ...externalActiveFiltersElements]

    allActiveFiltersElements.forEach(element => {
      element.setAttribute('aria-hidden', totalActiveFilters.length === 0 ? 'true' : 'false')
      element.textContent = `(${totalActiveFilters.length})`
    })

    // Update clear all buttons (both internal and external)
    const internalClearAllButtons = this.els['clear-all']?.elements || []
    const externalClearAllButtons = this.externalEls['clear-all']?.elements || []
    const allClearAllButtons = [...internalClearAllButtons, ...externalClearAllButtons]

    allClearAllButtons.forEach(button => {
      button.setAttribute('aria-hidden', totalActiveFilters.length === 0 ? 'true' : 'false')
    })

    document.dispatchEvent(new CustomEvent('productGrid:updated'))
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${
        searchParams && '?'.concat(searchParams.toString())
      }`,
    )
  }

  closeAllMobileFacets () {
    const summaryEls = this.externalEls['facet-summary'].elements
    for (const summaryEl of summaryEls) {
      const detailsEl = summaryEl.closest('details')
      if (!detailsEl) continue

      detailsEl.removeAttribute('open')
    }
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}
